<template>
	<vs-popup 
		:active.sync="popupActive" 
		title="Ajouter les backlinks"
		@close="closePopup">
		
		<div class="popup-content">
			<!-- Selection Mode -->
			<div class="selection-mode mb-4">
				<vs-radio 
					v-model="selectionMode" 
					vs-value="existing"
					class="mb-2">
					Sélectionner une liste existante
				</vs-radio>
				<vs-radio 
					v-model="selectionMode" 
					vs-value="new">
					Créer une nouvelle liste
				</vs-radio>
			</div>

			<!-- Existing List Selection -->
			<div v-if="selectionMode === 'existing'" class="existing-list-section">
				<vs-select
					v-model="selectedListId"
					label="Sélectionner une liste"
					class="w-full mb-4"
					placeholder="Choisir une liste">
					<vs-select-item
						v-for="list in existingLists"
						:key="list.id"
						:value="list.id"
						:text="`${list.name} - ${list.society_name || 'Sans société'}`">
						{{ list.name }} - {{ list.society_name || 'Sans société' }} ({{ list.count || 0 }} backlinks)
					</vs-select-item>
				</vs-select>
			</div>

			<!-- New List Creation -->
			<div v-if="selectionMode === 'new'" class="new-list-section">
				<vs-input
					v-model="newListName"
					label="Nom de la liste"
					placeholder="Entrez le nom de la nouvelle liste"
					class="w-full mb-4"
					@keyup.enter="validateAndSubmit"/>
				
				<vs-select
					v-model="selectedSocietyId"
					label="Société"
					class="w-full mb-4"
					autocomplete
					placeholder="Sélectionner une société">
					<vs-select-item
						v-for="society in societies"
						:key="society.id"
						:value="society.id"
						:text="society.name">
						{{ society.name }}
					</vs-select-item>
				</vs-select>
			</div>

			<!-- Error Message -->
			<div v-if="errorMessage" class="error-message mb-4">
				<vs-alert color="danger" :active="true">
					{{ errorMessage }}
				</vs-alert>
			</div>

			<!-- Action Buttons -->
			<div class="popup-footer">
				<vs-button 
					@click="closePopup" 
					color="dark" 
					type="border">
					Annuler
				</vs-button>
				<vs-button
					@click="validateAndSubmit"
					color="primary"
					:disabled="!isValid || isSubmitting"
					class="btn-submit">
					<span v-if="isSubmitting" class="flex items-center">
						<span class="btn-spinner mr-2"></span>
						Chargement...
					</span>
					<span v-else>
						{{ selectionMode === 'new' ? 'Créer et ajouter' : 'Ajouter' }}
					</span>
				</vs-button>
			</div>
		</div>
	</vs-popup>
</template>

<script>
import { Backlinks } from "@/entities/Backlinks";
import Society from "@/entities/Society";

export default {
	name: "BacklinksListSelectionPopup",
	props: {
		popupActive: {
			type: Boolean,
			required: true
		},
		selectedBacklinks: {
			type: Array,
			required: true
		}
	},
	data() {
		return {
			selectionMode: 'existing',
			selectedListId: null,
			newListName: '',
			selectedSocietyId: null,
			existingLists: [],
			societies: [],
			errorMessage: '',
			isLoading: false,
			isSubmitting: false
		};
	},
	computed: {
		isValid() {
			if (this.selectionMode === 'existing') {
				return this.selectedListId !== null;
			} else {
				return this.newListName.trim() !== '' && this.selectedSocietyId !== null;
			}
		}
	},
	watch: {
		popupActive(val) {
			if (val) {
				this.loadData();
			}
		}
	},
	mounted() {
		if (this.popupActive) {
			this.loadData();
		}
	},
	methods: {
		async loadData() {
			try {
				this.isLoading = true;
				
				// Load existing lists
				const listsResponse = await Backlinks.getAllLists();
				this.existingLists = Array.isArray(listsResponse) ? listsResponse : (listsResponse.data || []);
				
				// Load societies
				Society.getAllSocities((err, res) => {
					if (!err && res) {
						this.societies = res.data || [];
					}
				});
				
			} catch (error) {
				console.error('Error loading data:', error);
			} finally {
				this.isLoading = false;
			}
		},
		
		async validateAndSubmit() {
			if (!this.isValid) {
				this.errorMessage = 'Veuillez remplir tous les champs requis';
				return;
			}

			try {
				this.isLoading = true;
				this.isSubmitting = true;
				this.errorMessage = '';
				
				let listId = this.selectedListId;
				
				// Create new list if needed
				if (this.selectionMode === 'new') {
					const newListData = {
						name: this.newListName.trim(),
						society_id: this.selectedSocietyId
					};
					
					const response = await Backlinks.createList(newListData);
					// Response format: {"success":true,"listId":2,"message":"Backlinks list created successfully"}
					listId = response.listId || response.id;
				}
				
				// Add selected backlinks to the list
				// selectedBacklinks contains the backlink names (domains)
				if (this.selectedBacklinks && this.selectedBacklinks.length > 0) {
					await Backlinks.addBacklinksToList(listId, this.selectedBacklinks);
				}
				
				// Success notification
				this.$vs.notify({
					title: 'Succès',
					text: this.selectionMode === 'new' 
						? `Liste "${this.newListName}" créée et ${this.selectedBacklinks.length} backlink(s) ajouté(s)`
						: `${this.selectedBacklinks.length} backlink(s) ajouté(s) à la liste`,
					color: 'success',
					position: 'top-right'
				});
				
				// Emit success event and close
				this.$emit('success', {
					listId,
					listName: this.selectionMode === 'new' ? this.newListName : this.getListName(listId),
					backlinksCount: this.selectedBacklinks.length
				});
				
				this.closePopup();
				
			} catch (error) {
				console.error('Error submitting:', error);
				this.errorMessage = 'Erreur lors de l\'enregistrement';
				this.$vs.notify({
					title: 'Erreur',
					text: 'Impossible d\'enregistrer les données',
					color: 'danger',
					position: 'top-right'
				});
			} finally {
				this.isLoading = false;
				this.isSubmitting = false;
			}
		},
		
		getListName(listId) {
			const list = this.existingLists.find(l => l.id === listId);
			return list ? list.name : '';
		},
		
		closePopup() {
			this.resetForm();
			this.$emit('close');
		},
		
		resetForm() {
			this.selectionMode = 'existing';
			this.selectedListId = null;
			this.newListName = '';
			this.selectedSocietyId = null;
			this.errorMessage = '';
			this.isSubmitting = false;
		}
	}
};
</script>

<style scoped>
.popup-content {
	padding: 20px;
	min-width: 500px;
}

.selection-mode {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.existing-list-section,
.new-list-section {
	padding: 15px;
	background: #f5f5f5;
	border-radius: 8px;
	margin-top: 15px;
}

.popup-footer {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
	margin-top: 20px;
	padding-top: 20px;
	border-top: 1px solid #e0e6ed;
}

.error-message {
	margin-top: 15px;
}

.w-full {
	width: 100%;
}

.mb-2 {
	margin-bottom: 0.5rem;
}

.mb-4 {
	margin-bottom: 1rem;
}

.btn-spinner {
	display: inline-block;
	width: 14px;
	height: 14px;
	border: 2px solid rgba(255, 255, 255, 0.4);
	border-top-color: #fff;
	border-radius: 50%;
	animation: spin 0.6s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

.btn-submit .flex {
	display: flex;
	align-items: center;
	justify-content: center;
}
</style>